import React from "react";
import styled from "styled-components";
import countryList from "../../utils/countrylist.js";
import PersonalInfoSection from "./PersonalData.jsx";
import AffiliationSection from "./AffiliationSection.jsx";
import AbstractSection from "./AbstractSection.jsx";

const RegistrationForm = ({ formData, setFormData, handleChange, handleSubmit, isSubmitting, hasAbstract, handleDeleteAbstract }) => {
    return (
        <Container>
            <div className="registration-header">
                <h3>Register now</h3>
                <h6>Please fill out the form to register for the IUMRS-ICAM 2025 Conference</h6>
            </div>
            <div className="events-registration-form">
                <form onSubmit={handleSubmit}>
                    <div className="form-row">
                        <div className="form-column">
                            <label htmlFor="registration_category">Registration Category*</label>
                            <select 
                                id="registration_category" 
                                name="registration_category" 
                                value={formData.registration_category} 
                                onChange={handleChange}
                                required
                            >
                                <option value="student">Student</option>
                                <option value="academia">Academia</option>
                                <option value="industry">Industry</option>
                            </select>
                        </div>
                        <div className="form-column">
                            <label htmlFor="registration_timeframe">Registration Timeframe*</label>
                            <select 
                                id="registration_timeframe" 
                                name="registration_timeframe" 
                                value={formData.registration_timeframe} 
                                onChange={handleChange}
                                required
                            >
                                <option value="regular">Regular</option>
                                <option value="late-onsite">Late/Onsite</option>
                            </select>
                        </div>
                    </div>

                    {/* Personal Information Section */}
                    <PersonalInfoSection 
                        formData={formData} 
                        handleChange={handleChange} 
                    />
                    
                    {/* Affiliation Address Section */}
                    <AffiliationSection 
                        formData={formData} 
                        handleChange={handleChange} 
                        countryList={countryList}
                    />
                    
                    {/* Abstract Section */}
                    <AbstractSection 
                        formData={formData} 
                        handleChange={handleChange} 
                        hasAbstract={hasAbstract}
                        handleDeleteAbstract={handleDeleteAbstract}
                    />
                    
                    <div className="package-options">
                        <h4>Additional Options</h4>
                        
                        <div className="options-container">
                            <div className="checkbox-item">
                                <input 
                                    type="checkbox" 
                                    id="is_workshop" 
                                    name="is_workshop" 
                                    checked={formData.is_workshop} 
                                    onChange={handleChange}
                                />
                                <label htmlFor="is_workshop" className="checkbox-label">Register for pre-conference workshop</label>
                            </div>
                            <div className="checkbox-item">
                                <input 
                                    type="checkbox" 
                                    id="banquet" 
                                    name="banquet" 
                                    checked={formData.banquet} 
                                    onChange={handleChange}
                                />
                                <label htmlFor="banquet" className="checkbox-label">Add banquet package</label>
                            </div>
                            
                            <div className="checkbox-item">
                                <input 
                                    type="checkbox" 
                                    id="excursion" 
                                    name="excursion" 
                                    checked={formData.excursion} 
                                    onChange={handleChange}
                                />
                                <label htmlFor="excursion" className="checkbox-label">Add excursion package</label>
                            </div>
                        </div>
                    </div>

                    { formData.is_workshop && (
                        <div className="package-options">
                            <div className="">
                                <label htmlFor="workshop_category">Select at least one workshop to attend*</label>
                                <div className="workshop-options">
                                    {[
                                        { value: "electrospining_and_nanofiber_synthesis", label: "Electrospining and Nanofiber Synthesis" },
                                        { value: "metrohm_electrochemistry_workshop", label: "Metrohm Electrochemistry Workshop" },
                                        { value: "complex_geoparticles_synthesis", label: "Complex Geo-Particles Synthesis" },
                                        { value: "winter_school_on_sustainable_and_renewable_energy", label: "Winter School on Sustainable and Renewable energy" },
                                        { value: "impact_of_material_science_and_engineering_on_the_society", label: "Impact of Material Science and Engineering on the Society" }
                                    ].map(option => (
                                        <label key={option.value} className="workshop-option">
                                            <input
                                                type="checkbox"
                                                name="workshop_category"
                                                value={option.value}
                                                checked={formData.workshop_category?.includes(option.value)}
                                                onChange={(e) => {
                                                const { value, checked } = e.target;
                                                setFormData(prev => {
                                                    const updated = checked
                                                    ? [...(prev.workshop_category || []), value]
                                                    : (prev.workshop_category || []).filter(item => item !== value);

                                                    // If the array is empty, delete the field entirely
                                                    if (updated.length === 0) {
                                                        const { workshop_category, ...rest } = prev;
                                                        return rest;
                                                    }
                                                    
                                                    return { ...prev, workshop_category: updated };
                                                });
                                                }}
                                            />
                                            <span className="workshop-label">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>)}
                    
                    <button className="button" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                </form>
            </div>
        </Container>
    );
};

const Container = styled.div`
    .checkbox-item {
        display: flex;
        align-items: center;
        margin-bottom: 0.5em;
        
        input[type="checkbox"] {
            width: 18px;
            height: 18px;
            margin: 0;
            margin-right: 0.5em;
            cursor: pointer;
        }
        
        .checkbox-label {
            margin: 0;
            font-weight: 400;
            cursor: pointer;
        }
    }

    .workshop-options {
        display: flex;
        flex-direction: column;
        gap: 5px;
        margin: 1rem 0 0 0rem;
    }

    .workshop-option {
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: transform 0.1s ease;
    }

    .workshop-option:hover {
        transform: translateX(2px);
        }

    .workshop-option input[type="checkbox"] {
        width: 16px;
        height: 16px;
        margin: 0 10px 0 0;
        accent-color: #3182ce;
        cursor: pointer;
        }

    .workshop-label {
        font-size: 14px;
        font-weight: 400;
        display: inline-block;
        line-height:  1;
        }

    .registration-header {
        margin-bottom: 1.5em;

        h3 {
            margin-bottom: 0.2em;
            color: #db0f0f;
            font-size: 1.8em;
            font-weight: 700;
        }
        
        h6 {
            margin-top: 0;
            padding: 0;
            color: #6a7285;
            font-weight: 400;
            font-size: 1rem;
        }
    }
    
    .events-registration-form {
        display: flex;
        flex-direction: column;

        .form-row {
            display: flex;
            gap: 15px;
            margin-bottom: 0.5em;
            width: 100%;
        }

        .form-column {
            flex: 1;
            min-width: 0;
        }

        input, select {
            width: 100%;
            height: 2.8em;
            padding: 0.5em 0.8em;
            margin-bottom: 1.2em;
            color: #333;
            border: 1px solid #ddd;
            border-radius: 6px;
            font-size: 1em;
            box-sizing: border-box;
            transition: border-color 0.2s, box-shadow 0.2s;
        }

        label {
            color: #333;
            font-weight: 600;
            display: block;
            margin-bottom: 0.4em;
        }
        
        h4 {
            color: #333;
            margin-top: 1em;
            margin-bottom: 0.8em;
            padding-bottom: 0.5em;
            border-bottom: 1px solid #eee;
        }

        
        .package-options {
            border: 1px solid #eee;
            padding: 1.2em;
            border-radius: 8px;
            margin-top: 1em;
            margin-bottom: 1.5em;
            background-color: #fafafa;
            
            h4 {
                margin-top: 0;
                border-bottom: none;
                padding-bottom: 0.2em;
                font-weight: 500;
            }
            
            .options-container {
                display: flex;
                flex-wrap: wrap;
                gap: 1.5em;
                
                
            }
        }
    }
    
    .button {
        margin-top: 1.5em;
        background-color: #CA9D3B;
        width: 8em;
        height: 3em;
        color: white;
        font-size: 1em;
        font-weight: 600;
        border: none;
        border-radius: 10px;
        cursor: pointer;
        transition: background-color 0.2s ease;
        
        &:hover {
            background-color: #b78d2e;
        }
    }
    
    @media only screen and (max-width: 768px) {
        .events-registration-form {
            .form-row {
                flex-direction: column;
                gap: 0;
            }
            
            .package-options {
                .options-container {
                    flex-direction: column;
                    gap: 0.5em;
                }
            }
        }
    }
`;

export default RegistrationForm;